import React, { useState, useEffect } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import moment from "moment";

/**
 * ToggleableDate component that displays date in three formats: regular, countdown, or 24-hour
 * @param {Date|string} date - The date to display
 * @param {string} defaultFormat - "date", "countdown", or "24hour"
 * @param {function} onFormatChange - Optional callback when format changes
 */
const ToggleableDate = ({ date, defaultFormat = "date", onFormatChange }) => {
  // Initialize format from localStorage or use default
  const [displayFormat, setDisplayFormat] = useState(() => {
    const savedFormat = localStorage.getItem("dateDisplayFormat");
    return savedFormat || defaultFormat;
  });

  // Listen for format change events from other instances
  useEffect(() => {
    // Create custom event handler to sync with other instances
    const handleFormatChange = (event) => {
      setDisplayFormat(event.detail);
    };

    // Add event listener for format changes
    window.addEventListener("dateFormatChanged", handleFormatChange);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("dateFormatChanged", handleFormatChange);
    };
  }, []);

  // Format the date according to the current display preference
  const getFormattedDate = () => {
    if (!date) return "";

    const eventDate = moment(date);

    switch (displayFormat) {
      case "countdown": {
        const now = moment();
        const duration = moment.duration(eventDate.diff(now));

        // Only show non-zero values
        const days = Math.floor(duration.asDays());
        const hours = duration.hours();
        const minutes = duration.minutes();

        // Build the output string with only non-zero values
        let output = [];
        if (days > 0) output.push(`${days}d`);
        if (hours > 0) output.push(`${hours}h`);
        if (minutes > 0) output.push(`${minutes}m`);

        // If all values are zero or negative, show "Started"
        if (output.length === 0 || duration.asMinutes() <= 0) {
          return "Started";
        }

        return output.join(" ");
      }
      case "24hour":
        // 24-hour format
        return eventDate.local().format("MMM Do, HH:mm");
      case "date":
      default:
        // Regular 12-hour format
        return eventDate.local().format("MMM Do, h:mma");
    }
  };

  // Cycle through the three formats
  const toggleFormat = () => {
    let newFormat;
    switch (displayFormat) {
      case "date":
        newFormat = "countdown";
        break;
      case "countdown":
        newFormat = "24hour";
        break;
      case "24hour":
      default:
        newFormat = "date";
        break;
    }

    setDisplayFormat(newFormat);

    // Save to localStorage
    localStorage.setItem("dateDisplayFormat", newFormat);

    // Dispatch custom event to notify other instances
    window.dispatchEvent(
      new CustomEvent("dateFormatChanged", {
        detail: newFormat,
      })
    );

    // Call the callback if provided
    if (onFormatChange) {
      onFormatChange(newFormat);
    }
  };

  const getTooltipText = () => {
    switch (displayFormat) {
      case "date":
        return "Click to switch to countdown format";
      case "countdown":
        return "Click to switch to 24-hour format";
      case "24hour":
        return "Click to switch to regular format";
      default:
        return "Click to toggle display format";
    }
  };

  return (
    <Tooltip title={getTooltipText()}>
      <Typography
        variant="body2"
        onClick={toggleFormat}
        sx={{
          cursor: "pointer",
          "&:hover": {
            color: "primary.main",
            textDecoration: "underline",
          },
        }}
      >
        {getFormattedDate()}
      </Typography>
    </Tooltip>
  );
};

export default ToggleableDate;
